import { useEffect, useId, useMemo, useRef } from 'react'
import { cn } from '../../../../lib/utils'
import { createNadDebug } from '../../../../utils/debugLog'
import { useWaveform, WaveformBars } from './AudioWaveformShared'

const audioReferenceThumbnailLog = createNadDebug('dw:audio-reference-thumbnail')
const DEFAULT_THUMBNAIL_BAR_COUNT = 12
const FALLBACK_BAR_AMPS = [0.28, 0.48, 0.74, 0.42, 0.62, 0.9, 0.52, 0.34, 0.66, 0.44, 0.8, 0.56]

export type AudioReferenceThumbnailSurface =
  | 'video-editor-reference'
  | 'config-reference-row'
  | 'quick-info-reference'
  | 'reference-row'
  | 'node-selector-grid'
  | 'primary-voice-reference'
  | 'selected-reference-summary'

type AudioReferenceThumbnailLabelPlacement = 'overlay' | 'none'

export interface AudioReferenceThumbnailProps {
  label: string
  nativeTitle?: boolean
  className?: string
  testId?: string
  url?: string | null
  barCount?: number
  labelPlacement?: AudioReferenceThumbnailLabelPlacement
  referenceNodeId?: string
  surface: AudioReferenceThumbnailSurface
}

export function AudioReferenceThumbnail({
  label,
  nativeTitle = true,
  className,
  testId,
  url = null,
  barCount = DEFAULT_THUMBNAIL_BAR_COUNT,
  labelPlacement = 'overlay',
  referenceNodeId,
  surface,
}: AudioReferenceThumbnailProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const renderSequenceRef = useRef(0)
  const componentInstanceId = useId()
  const decodedBars = useWaveform(url, barCount)
  const fallbackBars = useMemo(
    () => Array.from({ length: barCount }, (_, index) => FALLBACK_BAR_AMPS[index % FALLBACK_BAR_AMPS.length]),
    [barCount],
  )
  const bars = decodedBars.length > 0 ? decodedBars : fallbackBars
  const logSurface = surface.replace(/-/g, '_')

  useEffect(() => {
    renderSequenceRef.current += 1
    const bounds = containerRef.current?.getBoundingClientRect()
    const widthPx = bounds ? Math.round(bounds.width) : null
    const heightPx = bounds ? Math.round(bounds.height) : null
    const isSquare = widthPx !== null && heightPx !== null ? Math.abs(widthPx - heightPx) <= 1 : null

    audioReferenceThumbnailLog({
      event: `audio-reference-thumbnail:${logSurface}:square-visible`,
      trace_id: `audio-reference-thumbnail:square:${surface}:${referenceNodeId ?? 'unknown'}`,
      reference_node_id: referenceNodeId ?? null,
      reference_key: referenceNodeId ?? 'unknown',
      surface: logSurface,
      preview_surface: surface,
      thumbnail_variant: 'square-waveform',
      has_audio_url: Boolean(url),
      waveform_status: decodedBars.length > 0 ? 'decoded' : 'fallback',
      label_placement: labelPlacement,
      decoded_bar_count: decodedBars.length,
      bar_count: barCount,
      width_px: widthPx,
      height_px: heightPx,
      aspect_ratio: isSquare === null ? 'pending-layout' : isSquare ? '1:1' : 'not-square',
      square_contract: isSquare ?? true,
      render_reason: renderSequenceRef.current === 1 ? 'mount' : 'update',
      render_sequence: renderSequenceRef.current,
      component_instance_id: componentInstanceId,
      dedupe_key: `audio-reference-thumbnail:square:${surface}:${referenceNodeId ?? 'unknown'}`,
    })
  }, [barCount, componentInstanceId, decodedBars.length, labelPlacement, logSurface, referenceNodeId, surface, url])

  return (
    <div
      ref={containerRef}
      title={nativeTitle ? label : undefined}
      role="img"
      aria-label={label}
      data-testid={testId}
      data-reference-media-type="audio"
      data-reference-node-id={referenceNodeId}
      data-reference-surface={surface}
      data-reference-thumbnail-shape="square"
      data-reference-label-placement={labelPlacement}
      className={cn(
        'relative flex aspect-square min-w-0 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-input-border bg-input-bg p-1 text-primary',
        className,
      )}
    >
      <WaveformBars
        bars={bars}
        barCount={barCount}
        color="currentColor"
        opacity={decodedBars.length > 0 ? 0.75 : 0.55}
        emptyOpacity={0.35}
        className={cn('h-full w-full', labelPlacement === 'overlay' ? 'pb-2' : '')}
      />
      {labelPlacement === 'overlay' ? (
        <span className="absolute inset-x-0 bottom-0 block truncate bg-input-bg/90 px-0.5 py-px text-center text-xxs font-semibold leading-none text-primary">
          {label}
        </span>
      ) : null}
    </div>
  )
}
